// IndexedDB-backed pending voucher queue. Auto-syncs when online.
import { openDB, type IDBPDatabase } from "idb";
import { supabase } from "@/integrations/supabase/client";

export interface QueuedVoucher {
  id: string;
  role: "sender" | "receiver";
  payload: any; // full signed voucher object
  receiver_ack_signature?: string;
  receiver_device_id?: string;
  created_at: number;
  last_try_at?: number;
  last_error?: string;
  status: "queued" | "syncing" | "settled" | "failed";
}

let dbPromise: Promise<IDBPDatabase> | null = null;
function db() {
  if (!dbPromise) {
    dbPromise = openDB("cashisle_ble", 1, {
      upgrade(d) {
        d.createObjectStore("vouchers", { keyPath: "id" });
      },
    });
  }
  return dbPromise;
}

export async function enqueueVoucher(v: QueuedVoucher) {
  const d = await db();
  await d.put("vouchers", v);
}

export async function listQueued(): Promise<QueuedVoucher[]> {
  const d = await db();
  return (await d.getAll("vouchers")) as QueuedVoucher[];
}

export async function removeVoucher(id: string) {
  const d = await db();
  await d.delete("vouchers", id);
}

export async function syncOne(v: QueuedVoucher): Promise<{ ok: boolean; error?: string; tx_id?: string }> {
  const d = await db();
  await d.put("vouchers", { ...v, status: "syncing", last_try_at: Date.now() });
  const { data, error } = await supabase.functions.invoke("ble-voucher-settle", {
    body: {
      voucher_id: v.id,
      voucher: v.payload,
      receiver_ack_signature: v.receiver_ack_signature,
      receiver_device_id: v.receiver_device_id,
    },
  });
  const err = (error as any)?.message || (data as any)?.error;
  if (err) {
    await d.put("vouchers", { ...v, status: "failed", last_error: err, last_try_at: Date.now() });
    return { ok: false, error: err };
  }
  await d.delete("vouchers", v.id);
  return { ok: true, tx_id: (data as any)?.tx_id };
}

export async function syncAll() {
  const items = await listQueued();
  const results: Array<{ id: string; ok: boolean; error?: string }> = [];
  for (const v of items) {
    if (v.status === "settled") continue;
    const r = await syncOne(v);
    results.push({ id: v.id, ok: r.ok, error: r.error });
  }
  return results;
}

let installed = false;
export function installAutoSync() {
  if (installed) return;
  installed = true;
  const trigger = () => { void syncAll(); };
  window.addEventListener("online", trigger);
  // Periodic safety net
  setInterval(trigger, 60_000);
}
